/* eslint-disable @typescript-eslint/no-explicit-any */
import { isOfflineBasemap } from '@/config/basemaps'
import type Basemap from '@/models/interfaces/map/Basemap'
import type PersistentGeoJSON from '@/models/interfaces/map/PersistentGeoJSON'
import { buildOfflineBasemapStyle } from '@/services/map/OfflineBasemapStyle'
import { kml } from '@tmcw/togeojson'
import axios from 'axios'
import JSZip from 'jszip'
import type { Map } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'

const DRAG_DROP_SOURCE_ID = 'drag-drop-geojson-source'
const DRAG_DROP_LAYER_FILL_ID = 'drag-drop-geojson-fill'
const DRAG_DROP_LAYER_LINE_ID = 'drag-drop-geojson-line'
const DRAG_DROP_LAYER_CIRCLE_ID = 'drag-drop-geojson-circle'

const USER_GEOJSON_PREFIX = 'user-geojson-'
const USER_LAYER_FILL_SUFFIX = '-fill'
const USER_LAYER_LINE_SUFFIX = '-line'
const USER_LAYER_CIRCLE_SUFFIX = '-circle'
const USER_LAYER_CIRCLE_HIT_SUFFIX = '-circle-hit'
const USER_LAYER_LINE_HIT_SUFFIX = '-line-hit'

export interface UserGeoJsonLayerIds {
  sourceId: string
  layerIds: string[]
  /** Transparent oversized hit layers for easier click/hover detection. */
  hitLayerIds: string[]
}

export default class MapService {
  static getGeojson(data: any[]): PersistentGeoJSON {
    const geojson: any = []
    Array.prototype.forEach.call(data, (item: any) => {
      if (item.geoData?.coords) {
        geojson.push({
          id: item.id,
          properties: {
            id: item.id,
            name: item.name,
          },
          geometry: {
            type: 'Point',
            coordinates: [item.geoData.coords.lng, item.geoData.coords.lat],
          },
        })
      }
    })

    return {
      type: 'FeatureCollection',
      features: geojson,
      isPersistent: true,
    }
  }

  static getBounds(features: any) {
    const coordinates = features.map((f: any) => f.geometry.coordinates)
    return coordinates.reduce(
      (bounds: any, coord: any) => {
        return bounds.extend(coord)
      },
      new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
    )
  }

  static isLoaded(map: Map | null | undefined, callback: () => void) {
    if (map) {
      if (map?.loaded()) {
        callback()
      } else {
        map.on('load', () => {
          callback()
        })
      }
    }
  }

  /** Style complet du fond : construit localement hors ligne, téléchargé sinon. */
  static async loadStyle(basemap: Basemap): Promise<maplibregl.StyleSpecification> {
    if (isOfflineBasemap(basemap)) return buildOfflineBasemapStyle()
    const result = await axios.get<maplibregl.StyleSpecification>(this.getStyle(basemap))
    return this.resolveMapboxStyleUrls(result.data)
  }

  static getStyle(basemap: Basemap) {
    const MAPBOX_STYLE_PREFIX = 'mapbox://styles/'
    if (basemap.style.startsWith(MAPBOX_STYLE_PREFIX)) {
      const stylePath = basemap.style.slice(MAPBOX_STYLE_PREFIX.length)
      return `https://api.mapbox.com/styles/v1/${stylePath}?access_token=${import.meta.env.VITE_MAPBOX_API_KEY}`
    }
    if (basemap.style.includes('api.mapbox.com')) {
      return `${basemap.style}?access_token=${import.meta.env.VITE_MAPBOX_API_KEY}`
    }
    return `${basemap.style}?key=${import.meta.env.VITE_MAPTILER_API_KEY}`
  }

  /**
   * Rewrite all mapbox:// protocol URLs in a Mapbox style to HTTPS equivalents
   * so MapLibre GL can resolve them.
   */
  static resolveMapboxStyleUrls(style: maplibregl.StyleSpecification): maplibregl.StyleSpecification {
    const token = import.meta.env.VITE_MAPBOX_API_KEY
    const qs = `access_token=${token}`

    if (typeof style.sprite === 'string' && style.sprite.startsWith('mapbox://sprites/')) {
      const path = style.sprite.slice('mapbox://sprites/'.length)
      const [owner, styleId] = path.split('/')
      style.sprite = `https://api.mapbox.com/styles/v1/${owner}/${styleId}/sprite?${qs}`
    }

    if (typeof style.glyphs === 'string' && style.glyphs.startsWith('mapbox://fonts/')) {
      const owner = style.glyphs.slice('mapbox://fonts/'.length).split('/')[0]
      style.glyphs = `https://api.mapbox.com/fonts/v1/${owner}/{fontstack}/{range}.pbf?${qs}`
    }

    if (style.sources) {
      for (const key of Object.keys(style.sources)) {
        const source = style.sources[key] as { url?: string; tiles?: string[] }
        if (source.url && source.url.startsWith('mapbox://')) {
          const tilesetIds = source.url.slice('mapbox://'.length)
          delete source.url
          source.tiles = [
            `https://a.tiles.mapbox.com/v4/${tilesetIds}/{z}/{x}/{y}.vector.pbf?${qs}`,
          ]
        }
      }
    }

    return style
  }

  private static readonly GEOJSON_VALID_TYPES = [
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
    'Feature',
    'FeatureCollection',
  ]

  private static readonly ACCEPTED_EXTENSIONS = /\.(geojson|json|kml|kmz)$/i

  private static getBaseName(fileName: string): string {
    return fileName.replace(this.ACCEPTED_EXTENSIONS, '') || fileName
  }

  private static isAcceptedFile(fileName: string): boolean {
    return this.ACCEPTED_EXTENSIONS.test(fileName)
  }

  private static normalizeToFeatureCollection(geojson: GeoJSON.GeoJSON): GeoJSON.FeatureCollection {
    if (geojson.type === 'FeatureCollection') {
      return geojson as GeoJSON.FeatureCollection
    }
    if (geojson.type === 'Feature') {
      return {
        type: 'FeatureCollection',
        features: [geojson as GeoJSON.Feature],
      }
    }
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: geojson as GeoJSON.Geometry,
        },
      ],
    }
  }

  private static parseKmlToGeojson(kmlText: string): GeoJSON.GeoJSON {
    const parser = new DOMParser()
    const doc = parser.parseFromString(kmlText, 'text/xml')
    const parsed = kml(doc as unknown as Document)
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid KML: conversion produced no GeoJSON.')
    }
    const type = (parsed as { type?: string }).type
    if (!type || !this.GEOJSON_VALID_TYPES.includes(type)) {
      throw new Error(`Invalid KML: unknown or missing type "${type ?? 'undefined'}"`)
    }
    return parsed as GeoJSON.GeoJSON
  }

  /**
   * Reads a GeoJSON, KML, or KMZ file and returns layer name (filename without extension) and parsed GeoJSON.
   * @throws Error if file type is not accepted or content is invalid
   */
  static async parseGeoJsonFromFile(
    file: File,
  ): Promise<{ name: string; geojson: GeoJSON.GeoJSON }> {
    const name = this.getBaseName(file.name)
    const lower = file.name.toLowerCase()
    if (!this.isAcceptedFile(file.name)) {
      throw new Error('Only GeoJSON, KML, or KMZ files are accepted (.geojson, .json, .kml, .kmz).')
    }
    if (lower.endsWith('.kmz')) {
      const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as ArrayBuffer)
        reader.onerror = () => reject(reader.error)
        reader.readAsArrayBuffer(file)
      })
      const zip = await JSZip.loadAsync(buffer)
      const kmlEntry = Object.keys(zip.files).find((path) => path.toLowerCase().endsWith('.kml'))
      if (!kmlEntry) {
        throw new Error('KMZ archive contains no .kml file.')
      }
      const kmlText = await zip.files[kmlEntry].async('string')
      const geojson = this.normalizeToFeatureCollection(this.parseKmlToGeojson(kmlText))
      return { name, geojson }
    }
    if (lower.endsWith('.kml')) {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file, 'UTF-8')
      })
      const geojson = this.normalizeToFeatureCollection(this.parseKmlToGeojson(text))
      return { name, geojson }
    }
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(reader.error)
      reader.readAsText(file, 'UTF-8')
    })
    const parsed = JSON.parse(text)
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid JSON: root must be an object')
    }
    const type = parsed.type
    if (!this.GEOJSON_VALID_TYPES.includes(type)) {
      throw new Error(`Invalid GeoJSON: unknown or missing type "${type}"`)
    }
    return { name, geojson: parsed as GeoJSON.GeoJSON }
  }

  /**
   * Adds a GeoJSON source and layers to the map for drag-and-drop data.
   * Replaces existing drag-drop layer if present.
   * @param geojson - Valid GeoJSON FeatureCollection, Feature, or Geometry
   */
  static addGeoJsonToMap(map: Map | null | undefined, geojson: object): void {
    if (!map) return
    if (map.getSource(DRAG_DROP_SOURCE_ID)) {
      if (map.getLayer(DRAG_DROP_LAYER_FILL_ID)) map.removeLayer(DRAG_DROP_LAYER_FILL_ID)
      if (map.getLayer(DRAG_DROP_LAYER_LINE_ID)) map.removeLayer(DRAG_DROP_LAYER_LINE_ID)
      if (map.getLayer(DRAG_DROP_LAYER_CIRCLE_ID)) map.removeLayer(DRAG_DROP_LAYER_CIRCLE_ID)
      map.removeSource(DRAG_DROP_SOURCE_ID)
    }
    map.addSource(DRAG_DROP_SOURCE_ID, {
      type: 'geojson',
      data: geojson as any,
    })
    const indicatorMeta = { isPersistent: true, layerGroup: this.LAYER_GROUP.INDICATOR } as const
    map.addLayer({
      id: DRAG_DROP_LAYER_FILL_ID,
      type: 'fill',
      source: DRAG_DROP_SOURCE_ID,
      filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.4,
        'fill-outline-color': '#1d4ed8',
      },
      metadata: indicatorMeta,
    })
    map.addLayer({
      id: DRAG_DROP_LAYER_LINE_ID,
      type: 'line',
      source: DRAG_DROP_SOURCE_ID,
      filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
      },
      metadata: indicatorMeta,
    })
    map.addLayer({
      id: DRAG_DROP_LAYER_CIRCLE_ID,
      type: 'circle',
      source: DRAG_DROP_SOURCE_ID,
      filter: ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]],
      paint: {
        'circle-radius': 6,
        'circle-color': '#3b82f6',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
      metadata: indicatorMeta,
    })
  }

  /**
   * Adds a named user GeoJSON source and layers to the map (multiple user layers supported).
   * @param map - Map instance
   * @param geojson - Valid GeoJSON FeatureCollection, Feature, or Geometry
   * @param layerId - Unique id for this layer (e.g. UUID)
   * @returns Source and layer ids for later removal or visibility control
   */
  static addUserGeoJsonLayer(
    map: Map | null | undefined,
    geojson: object,
    layerId: string,
  ): UserGeoJsonLayerIds | null {
    if (!map) return null
    const sourceId = USER_GEOJSON_PREFIX + layerId
    const fillId = sourceId + USER_LAYER_FILL_SUFFIX
    const lineId = sourceId + USER_LAYER_LINE_SUFFIX
    const circleId = sourceId + USER_LAYER_CIRCLE_SUFFIX
    const circleHitId = sourceId + USER_LAYER_CIRCLE_HIT_SUFFIX
    const lineHitId = sourceId + USER_LAYER_LINE_HIT_SUFFIX
    if (map.getSource(sourceId)) return null
    map.addSource(sourceId, {
      type: 'geojson',
      data: geojson as any,
    })
    const indicatorMeta = { isPersistent: true, layerGroup: this.LAYER_GROUP.INDICATOR } as const
    map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.4,
        'fill-outline-color': '#1d4ed8',
      },
      metadata: indicatorMeta,
    })
    map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
      },
      metadata: indicatorMeta,
    })
    map.addLayer({
      id: circleId,
      type: 'circle',
      source: sourceId,
      filter: ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]],
      paint: {
        'circle-radius': 6,
        'circle-color': '#3b82f6',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
      metadata: indicatorMeta,
    })
    // Transparent hit layer for points — large invisible circle for easier click detection.
    map.addLayer({
      id: circleHitId,
      type: 'circle',
      source: sourceId,
      filter: ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]],
      paint: {
        'circle-radius': 20,
        'circle-color': '#000000',
        'circle-opacity': 0,
        'circle-stroke-width': 0,
      },
      metadata: indicatorMeta,
    })
    // Transparent hit layer for lines — wide invisible line for easier click detection.
    map.addLayer({
      id: lineHitId,
      type: 'line',
      source: sourceId,
      filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
      paint: {
        'line-color': '#000000',
        'line-width': 20,
        'line-opacity': 0,
      },
      metadata: indicatorMeta,
    })
    return {
      sourceId,
      layerIds: [fillId, lineId, circleId, circleHitId, lineHitId],
      hitLayerIds: [fillId, circleHitId, lineHitId],
    }
  }

  /**
   * Removes a user GeoJSON source and its layers from the map.
   */
  static removeUserGeoJsonLayer(
    map: Map | null | undefined,
    sourceId: string,
    layerIds: string[],
  ): void {
    if (!map || !map.getSource(sourceId)) return
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    }
    map.removeSource(sourceId)
  }

  /**
   * Sets visibility of a user GeoJSON layer (all its sub-layers).
   */
  static setUserGeoJsonLayerVisibility(
    map: Map | null | undefined,
    layerIds: string[],
    visible: boolean,
  ): void {
    if (!map) return
    const visibility = visible ? 'visible' : 'none'
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    }
  }

  /**
   * Layer order (G8): 1) base map (bottom), 2) fond (commune, cadastre), 3) indicators (top).
   * Use when adding layers so fond stays below indicators.
   */
  static readonly LAYER_GROUP = {
    FOND: 'fond',
    INDICATOR: 'indicator',
  } as const

  /**
   * Returns the id of the first (bottom-most) layer with metadata.layerGroup === 'indicator'.
   * Use as beforeId when adding fond layers so they are inserted below indicator layers.
   */
  static getFirstIndicatorLayerId(map: Map | null | undefined): string | undefined {
    if (!map) return undefined
    const layers = map.getStyle().layers
    if (!layers?.length) return undefined
    const found = layers.find(
      (l) => (l as { metadata?: { layerGroup?: string } }).metadata?.layerGroup === 'indicator',
    )
    return found?.id
  }

  /**
   * Returns the id of the topmost basemap layer (last layer without overlay metadata).
   * Used to ensure overlay layers are added above the base map.
   */
  static getTopBasemapLayerId(map: Map | null | undefined): string | undefined {
    if (!map) return undefined
    const layers = map.getStyle().layers
    if (!layers?.length) return undefined
    for (let i = layers.length - 1; i >= 0; i--) {
      const meta = (layers[i] as { metadata?: { isPersistent?: boolean; layerGroup?: string } })
        ?.metadata
      if (!meta?.isPersistent && !meta?.layerGroup) return layers[i].id
    }
    return undefined
  }

  static async updateStyle(map: Map, basemap: Basemap) {
    const layers = map.getStyle().layers
    const sources = map.getStyle().sources

    const filteredLayers = layers.filter((l) => (l as { metadata?: { isPersistent?: boolean } })?.metadata?.isPersistent)
    const filteredSources: Record<string, maplibregl.SourceSpecification> = {}

    Object.keys(sources).forEach((key: string) => {
      if ((sources[key] as { url?: string })?.url) return
      filteredSources[key] = sources[key] as maplibregl.SourceSpecification
    })

    // G8: keep overlay order: fond first, then indicators (so base map stays at bottom after merge)
    const sortedOverlayLayers = [...filteredLayers].sort((a, b) => {
      const aIndicator =
        (a as { metadata?: { layerGroup?: string } }).metadata?.layerGroup === 'indicator' ? 1 : 0
      const bIndicator =
        (b as { metadata?: { layerGroup?: string } }).metadata?.layerGroup === 'indicator' ? 1 : 0
      return aIndicator - bIndicator
    })

    return this.loadStyle(basemap)
      .then((newStyle) => {
        newStyle.layers = [...(newStyle.layers ?? []), ...sortedOverlayLayers]
        newStyle.sources = { ...(newStyle.sources ?? {}), ...filteredSources }
        map.setStyle(newStyle)
      })
      .catch((error) => {
        console.error('[MapService] updateStyle failed', error)
        return Promise.reject(error)
      })
  }
}

export class IControl {
  _map: any
  _container: any
  control: any

  constructor(control: any) {
    this.control = control
  }

  onAdd(map: any) {
    this._map = map
    this._container = this.control.value.$el
    return this._container
  }
  onRemove() {
    if (this._container.parentNode == null) return
    this._container.parentNode.removeChild(this._container)
    this._map.value = undefined
  }
}
